export enum Operators {
	CONTAINS = 'contains',
	NOT_CONTAINS = 'not_contains',
	IS = 'is',
	IS_NOT = 'is_not',
	IN = 'in',
	NOT_IN = 'not_in',

	IS_EMPTY = 'is_empty',
	IS_NOT_EMPTY = 'is_not_empty',

	GT = 'gt',
	LT = 'lt',
	EQ = 'eq',
	LTE = 'lte',
	GTE = 'gte',
	NE = 'ne',
	BETWEEN = 'num_between',

	BL = 'bl',

	ANY_OF = 'array_any_of',
	ALL_OF = 'array_all_of',
	EXACT = 'array_exact',
	NONE_OF = 'array_none_of',

	DATE_BETWEEN = 'date_between',
	DATE_AFTER = 'date_after',
	DATE_BEFORE = 'date_before',
	DATE_AT = 'date_at',

	DESCENDANT_OF = 'descendant_of',
}

export const stringOperators = [
	Operators.CONTAINS,
	Operators.NOT_CONTAINS,
	Operators.IS,
	Operators.IS_NOT,
	Operators.IN,
	Operators.NOT_IN,
	Operators.IS_EMPTY,
	Operators.IS_NOT_EMPTY,
]
export const numericOperators = [
	Operators.GT,
	Operators.LT,
	Operators.EQ,
	Operators.LTE,
	Operators.GTE,
	Operators.NE,
	Operators.BETWEEN,
	Operators.IS_EMPTY,
	Operators.IS_NOT_EMPTY,
]
export const dateOperators = [
	Operators.DATE_AT,
	Operators.DATE_BEFORE,
	Operators.DATE_AFTER,
	Operators.DATE_BETWEEN,
	Operators.IS_EMPTY,
	Operators.IS_NOT_EMPTY,
]

export enum ValueType {
	/**
	 * String value type
	 * @example 'title'
	 */
	string = 'string',

	/**
	 * Float value type
	 * @example 1.2
	 * @deprecated use number instead
	 */
	float = 'float',

	/**
	 * Integer value type
	 * @example 1
	 */
	integer = 'integer',

	/**
	 * Number value type
	 * @example 1.2, 1
	 */
	number = 'number',

	/**
	 * Boolean value type
	 * @example true, false
	 */
	boolean = 'boolean',

	/**
	 * Array value type
	 * @example ['a', 'b']
	 */
	array = 'array',

	/**
	 * Date time value type
	 * @example '2020-01-01T00:00:00'
	 */
	datetime = 'datetime',

	/**
	 * Date value type
	 * @example '2020-01-01'
	 */
	date = 'date',
}

export enum ValueEditorType {
	/**
	 * Select value editor type
	 * allows to select one value from the list
	 */
	select = 'select',

	/**
	 * Multiselect value editor type
	 * allows to select multiple values from the list
	 */
	multiselect = 'multiselect',

	/**
	 * Text value editor type
	 * allows to enter text value
	 */
	text = 'text',

	/**
	 * Number value editor type
	 * allows to enter number value
	 */
	number = 'number',

	/**
	 * Checkbox value editor type
	 * allows to select boolean value
	 */
	checkbox = 'checkbox',

	/**
	 * Checkbox tree value editor type
	 * allows to select multiple values from the tree of checkboxes
	 */
	checkboxTree = 'checkbox_tree',

	/**
	 * Date value editor type
	 * allows to select date value
	 */
	daterange = 'daterange',

	/**
	 * Date with time value editor type
	 * allows to select date and time values
	 */
	datetimerange = 'datetimerange',

	/**
	 * Date value editor type
	 * allows to select date value
	 */
	date = 'date',

	/**
	 * Percent value editor type
	 * allows to select percent value
	 */
	percent = 'percent',
}

export const operationsConfigs: Record<
	Operators,
	{
		operation: Operators
		label: string
		hasInput?: boolean
		defaultValue: any
	}
> = {
	[Operators.DATE_AFTER]: {
		operation: Operators.DATE_AFTER,
		label: 'after',
		hasInput: true,
		defaultValue: '',
	},
	[Operators.DATE_BEFORE]: {
		operation: Operators.DATE_BEFORE,
		label: 'before',
		hasInput: true,
		defaultValue: '',
	},
	[Operators.DATE_AT]: {
		operation: Operators.DATE_AT,
		label: 'at',
		hasInput: true,
		defaultValue: '',
	},
	[Operators.DATE_BETWEEN]: {
		operation: Operators.DATE_BETWEEN,
		label: 'between',
		hasInput: true,
		defaultValue: [],
	},
	[Operators.CONTAINS]: {
		operation: Operators.CONTAINS,
		label: 'contains',
		hasInput: true,
		defaultValue: '',
	},
	[Operators.NOT_CONTAINS]: {
		operation: Operators.NOT_CONTAINS,
		label: 'does not contain',
		hasInput: true,
		defaultValue: '',
	},
	[Operators.IS]: {
		operation: Operators.IS,
		label: 'is',
		hasInput: true,
		defaultValue: '',
	},
	[Operators.IN]: {
		operation: Operators.IN,
		label: 'in',
		hasInput: true,
		defaultValue: [],
	},
	[Operators.NOT_IN]: {
		operation: Operators.NOT_IN,
		label: 'not in',
		hasInput: true,
		defaultValue: [],
	},
	[Operators.IS_NOT]: {
		operation: Operators.IS_NOT,
		label: 'is not',
		hasInput: true,
		defaultValue: '',
	},
	[Operators.IS_EMPTY]: {
		operation: Operators.IS_EMPTY,
		label: 'is empty',
		defaultValue: '1',
	},
	[Operators.IS_NOT_EMPTY]: {
		operation: Operators.IS_NOT_EMPTY,
		label: 'is not empty',
		defaultValue: '1',
	},
	[Operators.GT]: {
		operation: Operators.GT,
		label: 'greater than',
		hasInput: true,
		defaultValue: '',
	},
	[Operators.LT]: {
		operation: Operators.LT,
		label: 'less than',
		hasInput: true,
		defaultValue: '',
	},
	[Operators.EQ]: {
		operation: Operators.EQ,
		label: 'equal',
		hasInput: true,
		defaultValue: '',
	},
	[Operators.LTE]: {
		operation: Operators.LTE,
		label: 'less or equal than',
		hasInput: true,
		defaultValue: '',
	},
	[Operators.GTE]: {
		operation: Operators.GTE,
		label: 'greater or equal than',
		hasInput: true,
		defaultValue: '',
	},
	[Operators.NE]: {
		operation: Operators.NE,
		label: 'not equal',
		hasInput: true,
		defaultValue: '',
	},
	[Operators.BL]: { operation: Operators.BL, label: 'bl', defaultValue: '1' },
	[Operators.ANY_OF]: {
		operation: Operators.ANY_OF,
		label: 'any of',
		hasInput: true,
		defaultValue: [],
	},
	[Operators.ALL_OF]: {
		operation: Operators.ALL_OF,
		label: 'all of',
		hasInput: true,
		defaultValue: [],
	},
	[Operators.EXACT]: {
		operation: Operators.EXACT,
		label: 'exact',
		hasInput: true,
		defaultValue: [],
	},
	[Operators.NONE_OF]: {
		operation: Operators.NONE_OF,
		label: 'none of',
		hasInput: true,
		defaultValue: [],
	},
	[Operators.BETWEEN]: {
		operation: Operators.BETWEEN,
		label: 'between',
		hasInput: true,
		defaultValue: [],
	},
	[Operators.DESCENDANT_OF]: {
		operation: Operators.DESCENDANT_OF,
		label: 'descendant of',
		hasInput: true,
		defaultValue: '',
	},
}
